import { useEffect, useState } from "react";

import { api, ApiError } from "../lib/api";
import { useAuth } from "../context/auth";
import { useT } from "../context/locale";
import type { TranslationKey } from "../i18n";
import { useToast } from "../context/toast";
import { centsToInput, money, parseMoney, signedMoney } from "../lib/format";
import { Button, ErrorNote, Modal } from "./ui";
import { MoneyField, SegmentedControl, TextAreaField } from "./ui/form";
import type { AdminUser } from "../types/api";

/*
 * One dialog for the three ways credit moves, because the thing an operator has
 * to get right is which of them this is:
 *
 *   Top up    — add an amount.       "MYR 500 came in."
 *   Withdraw  — subtract an amount.  "MYR 500 was paid out."
 *   Set       — state a figure.      "It should read MYR 500."
 *
 * Amounts for the first two, since that is what the operator actually knows; a
 * figure for the third, since a correction is about where you want to end up
 * rather than the arithmetic to get there. Keeping them in one dialog behind a
 * mode switch also keeps the players table to one button per row instead of
 * three, and puts the choice in front of the person making it.
 */

export type CreditMode = "top_up" | "set" | "withdraw";

const MODES: { id: CreditMode; adminOnly: boolean }[] = [
  { id: "top_up", adminOnly: true },
  { id: "set", adminOnly: true },
  { id: "withdraw", adminOnly: false },
];

/**
 * Which key carries each piece of copy for a mode.
 *
 * A lookup table rather than three branches in the body: the parallel wording
 * between the modes is the point, and it stays visible when the keys line up.
 */
const COPY: Record<
  CreditMode,
  {
    title: TranslationKey;
    blurb: TranslationKey;
    fieldLabel: TranslationKey;
    fieldHint?: TranslationKey;
    previewLabel: TranslationKey;
    reasonLabel: TranslationKey;
    placeholder: TranslationKey;
    submit: TranslationKey;
  }
> = {
  top_up: {
    title: "credit.topUp",
    blurb: "credit.topUpBlurb",
    fieldLabel: "credit.amountToAdd",
    previewLabel: "credit.afterTopUp",
    reasonLabel: "credit.reasonRef",
    placeholder: "credit.topUpPlaceholder",
    submit: "credit.recordTopUp",
  },
  withdraw: {
    title: "credit.withdraw",
    blurb: "credit.withdrawBlurb",
    fieldLabel: "credit.amountToDeduct",
    previewLabel: "credit.afterWithdrawal",
    reasonLabel: "common.reason",
    placeholder: "credit.withdrawPlaceholder",
    submit: "credit.recordWithdrawal",
  },
  set: {
    title: "credit.setBalance",
    blurb: "credit.setBlurb",
    fieldLabel: "credit.newBalance",
    fieldHint: "credit.exactHint",
    previewLabel: "credit.newBalance",
    reasonLabel: "common.reason",
    placeholder: "credit.setPlaceholder",
    submit: "credit.setBalance",
  },
};

/**
 * Adjusts a player's credit.
 *
 * `initialMode` picks the tab it opens on; the operator can switch without
 * closing. Support only ever sees Withdraw — it can only reduce a balance, by a
 * stated amount, with a reason, which makes it the safe half of the operation.
 */
export function CreditDialog({
  user,
  initialMode = "top_up",
  onClose,
  onDone,
}: {
  user: AdminUser | null;
  initialMode?: CreditMode;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const { can } = useAuth();
  const isAdmin = can("admin");
  const allowed = MODES.filter((m) => isAdmin || !m.adminOnly);

  const [mode, setMode] = useState<CreditMode>(initialMode);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const balance = user?.balanceCents ?? 0;
  const copy = COPY[mode];

  // Reset each time a player is handed in, and land on a mode this role has.
  useEffect(() => {
    if (!user) return;
    const start = isAdmin ? initialMode : "withdraw";
    setMode(start);
    setAmount(start === "set" ? centsToInput(user.balanceCents) : "");
    setReason("");
    setError(null);
  }, [user, initialMode, isAdmin]);

  function switchMode(next: CreditMode) {
    setMode(next);
    // "Set" wants the current figure to edit; the other two want an empty box.
    setAmount(next === "set" ? centsToInput(balance) : "");
    setError(null);
  }

  const cents = parseMoney(amount);
  const tooMuch = mode === "withdraw" && cents !== null && cents > balance;
  const negative = mode === "set" && cents !== null && cents < 0;
  const valid =
    cents !== null &&
    (mode === "set" ? cents >= 0 : cents > 0) &&
    !tooMuch &&
    reason.trim().length > 0;

  /** What the balance becomes, for the preview strip. */
  const projected =
    cents === null
      ? null
      : mode === "top_up"
        ? balance + cents
        : mode === "withdraw"
          ? balance - cents
          : cents;
  const delta = cents === null ? 0 : (projected ?? balance) - balance;

  function close() {
    setAmount("");
    setReason("");
    setError(null);
    onClose();
  }

  async function submit() {
    if (!user || !valid || cents === null) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === "top_up") {
        const result = await api.topUp(user.id, cents, reason.trim());
        toast.success(
          t("credit.topUpToast"),
          t("credit.topUpDetail", {
            amount: money(result.amountCents),
            name: user.username,
            balance: money(result.user.balanceCents),
          }),
        );
      } else if (mode === "withdraw") {
        const result = await api.withdraw(user.id, cents, reason.trim());
        toast.success(
          t("credit.withdrawToast"),
          t("credit.withdrawDetail", {
            amount: money(result.amountCents),
            name: user.username,
            balance: money(result.user.balanceCents),
          }),
        );
      } else {
        const result = await api.setBalance(user.id, cents, reason.trim());
        toast.success(
          t("credit.setToast"),
          t("credit.setDetail", {
            name: user.username,
            balance: money(result.user.balanceCents),
            delta: signedMoney(result.deltaCents),
          }),
        );
      }
      onDone();
      close();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : t("credit.failed"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={Boolean(user)}
      onClose={close}
      eyebrow={t("section.credits")}
      title={t("credit.title", {
        mode: t(copy.title),
        name: user?.username ?? "",
      })}
      width="30rem"
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            variant={mode === "withdraw" ? "primary" : "gold"}
            onClick={submit}
            loading={busy}
            disabled={!valid}
          >
            {t(copy.submit)}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {allowed.length > 1 ? (
          <SegmentedControl
            value={mode}
            onChange={switchMode}
            options={allowed.map((m) => ({
              value: m.id,
              label: t(COPY[m.id].title),
            }))}
            ariaLabel={t("credit.operation")}
          />
        ) : null}

        <p className="text-muted text-[13px]">{t(copy.blurb)}</p>

        <div
          className="flex items-center justify-between rounded-lg px-4 py-3"
          style={{ background: "var(--surface-inset)" }}
        >
          <div>
            <p className="eyebrow">Current balance</p>
            <p className="money mt-1 text-[16px]">{money(balance)}</p>
          </div>
          <span className="text-dim text-[18px]" aria-hidden>
            →
          </span>
          <div className="text-right">
            <p className="eyebrow">{t(copy.previewLabel)}</p>
            <p
              className="money mt-1 text-[16px]"
              style={{ color: tooMuch ? "var(--danger)" : "var(--accent)" }}
            >
              {projected === null ? "—" : money(projected)}
            </p>
          </div>
        </div>

        <MoneyField
          label={t(copy.fieldLabel)}
          required
          hint={copy.fieldHint ? t(copy.fieldHint) : undefined}
          error={
            tooMuch
              ? t("credit.tooMuch", { amount: money(balance) })
              : negative
                ? t("credit.nonNegative")
                : null
          }
          value={amount}
          onChange={setAmount}
          autoFocus
        />

        {mode === "set" && valid && delta !== 0 ? (
          <p
            className="rounded-lg px-3 py-2 text-[12px]"
            style={{
              background: delta > 0 ? "var(--success-bg)" : "var(--danger-bg)",
              color: delta > 0 ? "var(--success)" : "var(--danger)",
            }}
          >
            {t(delta > 0 ? "credit.willCredit" : "credit.willDebit", {
              amount: signedMoney(delta),
            })}
          </p>
        ) : null}

        <TextAreaField
          label={t(copy.reasonLabel)}
          required
          hint={t("common.ledgerHint")}
          value={reason}
          onChange={setReason}
          placeholder={t(copy.placeholder)}
        />

        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}
